import Cookies from 'js-cookie';

export const QUICK_START_COOKIE_NAME = 'qs_ctx';
const ENC_KEY_STORAGE_KEY = 'qs_enc_key';

export interface QuickStartCookieData {
  lockerId: number;
  email: string;
  password: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmTerm?: string | null;
  utmCampaign?: string | null;
}

function getOrCreateEncryptionKey(): string {
  let key = localStorage.getItem(ENC_KEY_STORAGE_KEY);
  if (!key) {
    key = crypto.randomUUID();
    localStorage.setItem(ENC_KEY_STORAGE_KEY, key);
  }
  return key;
}

async function deriveKey(passphrase: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey('raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode('qs-cookie-salt'), iterations: 100_000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function toBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const str = atob(b64);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i);
  }
  return bytes;
}

export async function setQuickStartCookie(data: QuickStartCookieData): Promise<void> {
  const enc = new TextEncoder();
  const iv: Uint8Array<ArrayBuffer> = new Uint8Array(12);
  crypto.getRandomValues(iv);
  const key = await deriveKey(getOrCreateEncryptionKey());

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(data)));

  Cookies.set(QUICK_START_COOKIE_NAME, `${toBase64(iv)}.${toBase64(new Uint8Array(ciphertext))}`, {
    expires: 30,
    sameSite: 'strict',
    secure: window.location.protocol === 'https:',
  });
}

export async function getQuickStartCookie(): Promise<QuickStartCookieData | null> {
  const passphrase = localStorage.getItem(ENC_KEY_STORAGE_KEY);
  if (!passphrase) return null;

  const raw = Cookies.get(QUICK_START_COOKIE_NAME);
  if (!raw) return null;

  const [ivB64, ctB64] = raw.split('.');
  if (!ivB64 || !ctB64) return null;

  try {
    const key = await deriveKey(passphrase);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(ivB64) }, key, fromBase64(ctB64));
    return JSON.parse(new TextDecoder().decode(plaintext)) as QuickStartCookieData;
  } catch {
    return null;
  }
}

export function deleteQuickStartCookie(): void {
  Cookies.remove(QUICK_START_COOKIE_NAME);
}
